import { useId, useMemo, useRef } from "react";
import { WAVES, framePaths, type WaveName } from "./ribbonWave";
import { useRibbonWave } from "./useRibbonWave";
import styles from "./JourneyBand.module.css";

// The journey from fb/web, drawn left to right across a 1440×900 box that stretches to the section.
const CURVES = {
  rise: "M -20 820 C 260 760 420 520 700 560 S 1120 260 1460 180",
  dip: "M -20 140 C 300 220 520 700 860 640 S 1240 380 1460 460",
  exit: "M -20 520 C 320 380 560 820 900 720 S 1260 260 1460 120",
  // Sweeps down the right side and settles at the button row of a page top.
  settle: "M -20 300 C 300 120 640 160 900 360 S 1240 760 1460 800",
};

type Tone = "dark" | "light";

// The values from the "Hero readability" board. Dark is petrol behind white text; light is cream.
const TONES = {
  dark: { blur: 4, opacity: 0.8, width: 6, shadow: "#fbf8f2", shadowOpacity: 0.08, shadowWidth: 28, tailOpacity: 0.8 },
  light: { blur: 2.5, opacity: 0.9, width: 5, shadow: "#0f2b3c", shadowOpacity: 0.06, shadowWidth: 26, tailOpacity: 0.9 },
};

// How much quiet tones a tone down: the same shape, only fainter.
const QUIET = { opacity: 0.45, tailOpacity: 0.45, shadowOpacity: 0.5 };
const times = (value: number, factor: number) => Math.round(value * factor * 100) / 100;

type JourneyBandProps = {
  tone?: Tone;
  /** One of the named curves or an own path in the 1440×900 box. */
  curve?: keyof typeof CURVES | (string & {});
  /** The sharp tail that points at the most important thing on the surface. */
  tail?: boolean;
  /** A fainter band for surfaces where it lies close to text, like the top of a goal. */
  quiet?: boolean;
  /** Moves the band with the wave engine, in that wave's own box. Without it nothing moves. */
  wave?: WaveName;
  /** The coral-to-amber line, blurred layer and tail. Without it only the shadow line is drawn. */
  line?: boolean;
  /** Scales the opacity of every layer; 1 is the tone as it is. The Journey Card uses 0.75. */
  strength?: number;
};

/**
 * The journey band as decor behind text: a blurred coral-to-amber layer, a faint wide shadow line
 * and an optional sharp tail over the last 30 %. With line={false} only the shadow line is left.
 * Nothing moves unless the band gets a wave.
 */
const JourneyBand = ({ tone = "dark", curve = "rise", tail = true, quiet = false, wave, line = true, strength = 1 }: JourneyBandProps) => {
  const id = `journey-${useId().replace(/:/g, "")}`;
  const d = curve in CURVES ? CURVES[curve as keyof typeof CURVES] : curve;
  const base = TONES[tone];
  const q = quiet ? QUIET : { opacity: 1, tailOpacity: 1, shadowOpacity: 1 };
  const v =
    quiet || strength !== 1
      ? {
          ...base,
          opacity: times(base.opacity, q.opacity * strength),
          tailOpacity: times(base.tailOpacity, q.tailOpacity * strength),
          shadowOpacity: times(base.shadowOpacity, q.shadowOpacity * strength),
        }
      : base;
  const r = wave ? WAVES[wave] : null;
  const start = useMemo(() => (r ? framePaths(r, 0) : null), [r]);
  const refs = { svg: useRef<SVGSVGElement>(null), shadow: useRef<SVGPathElement>(null), soft: useRef<SVGPathElement>(null), tail: useRef<SVGPathElement>(null) };
  useRibbonWave(refs, r);
  const path = { d: start?.main ?? d, fill: "none", strokeLinecap: "round", vectorEffect: "non-scaling-stroke" } as const;

  return (
    <svg
      ref={refs.svg}
      aria-hidden='true'
      className={styles.band}
      data-tone={tone}
      data-animated={r ? "" : undefined}
      viewBox={r?.viewBox ?? "0 0 1440 900"}
      preserveAspectRatio='none'
    >
      <defs>
        <linearGradient id={`${id}-soft`} x1='0' y1='0' x2='1' y2='0'>
          <stop offset='0%' stopColor='#f08a6c' stopOpacity='0' />
          <stop offset='40%' stopColor='#f08a6c' />
          <stop offset='100%' stopColor='#e8b86a' />
        </linearGradient>
        <linearGradient id={`${id}-tail`} x1='0' y1='0' x2='1' y2='0'>
          <stop offset='70%' stopColor='#f08a6c' stopOpacity='0' />
          <stop offset='80%' stopColor='#f08a6c' stopOpacity={v.tailOpacity} />
          <stop offset='100%' stopColor='#e8b86a' stopOpacity={v.tailOpacity} />
        </linearGradient>
      </defs>
      <path {...path} ref={refs.shadow} d={start?.shadow ?? d} className={styles.shadow} data-layer='shadow' stroke={v.shadow} strokeOpacity={v.shadowOpacity} strokeWidth={v.shadowWidth} />
      {line && <path {...path} ref={refs.soft} data-layer='soft' stroke={`url(#${id}-soft)`} strokeWidth={v.width} opacity={v.opacity} style={{ filter: `blur(${v.blur}px)` }} />}
      {line && tail && <path {...path} ref={refs.tail} data-layer='tail' stroke={`url(#${id}-tail)`} strokeWidth={2.5} />}
    </svg>
  );
};

/** The soft field behind a text block. Put it first in a block with `position: relative; isolation: isolate`. */
export const JourneyScrim = ({ tone = "dark" }: { tone?: Tone }) => <div aria-hidden='true' className={`${styles.scrim} ${styles[tone]}`} />;

export default JourneyBand;
